/**
 * ScreenRenderer - Renders assessment screens for participant survey taking
 * Matches the preview functionality from Assessment Builder
 * Reuses VAS, NRS, VRS scale components for eCOA compliance
 */

import React, { useState, useEffect, useRef } from 'react';
import VASSlider from '../Shared/VASSlider';
import NRSScale from '../Shared/NRSScale';
import VRSScale from '../Shared/VRSScale';
import SegmentedControl from '../Shared/SegmentedControl';
import EmojiFaceScale from '../Shared/EmojiFaceScale';
import ThermometerScale from '../Shared/ThermometerScale';
import SteppedSlider from '../Shared/SteppedSlider';
import BodyMap from './BodyMap/BodyMap';
import { AlertCircle } from 'lucide-react';
import {
  getFallbackMeasuredOptionBoxWidth,
  getOptionBoxWidth,
  getOptionButtonColors,
  getOptionButtonSizingStyle,
  getOptionsGroupLayout,
  getOptionSpacing,
  getOptionTextAlign
} from '../../utils/responseOptionAppearance';

/**
 * Helper function to render question response inputs based on question type and layout config
 * Supports all eCOA question types:
 * - Ordinal (Likert-type): Radio buttons / Vertical List
 * - NRS (Numeric Rating Scale): Button Grid (0-10)
 * - VAS (Visual Analog Scale): Slider Bar
 * - Nominal (Categories): Checkboxes for multi-select, Radio for single-select
 * - Free Text: Text Input Field (250 char default limit)
 * - Yes/No: Binary radio buttons
 * - Numeric: Number input
 * - Date: Date picker
 * - Boolean: Yes/No or True/False buttons
 */
// Whether a single-select option should render as selected. Selection is keyed off
// the clicked option index (recordedIdx) so options sharing a value don't all
// highlight (FECOA-1521). Before any click is recorded (restore), fall back to the
// saved value, matching only the first option that carries it to keep one highlight.
const isSingleSelectOptionSelected = (recordedIdx, idx, savedValue, optionValue, isFirstValueMatch) => {
  if (recordedIdx !== undefined && recordedIdx !== null) {
    return recordedIdx === idx;
  }
  if (savedValue === undefined || savedValue === null || savedValue === '') return false;
  const valueMatches = savedValue == optionValue || String(savedValue) === String(optionValue);
  return valueMatches && isFirstValueMatch;
};

const renderQuestionResponse = (element, localResponses, handleResponseChange, disabled, selectedOptionIndex = {}) => {
  const layout = element.responseConfig?.layout;
  // Accept both runtime (element.questionType, set by ParticipantScreenSerializer)
  // and builder (element.data.type / data.question_type / itemData.type) shapes so
  // PreviewMode and SurveyTaking dispatch to the same renderer branch. Without
  // this fallback, free-text/numeric/date questions in PreviewMode fall through
  // to `return null` because element.questionType is undefined.
  const questionType = element.questionType
    || element.data?.type
    || element.data?.question_type
    || element.data?.response_type
    || element.itemData?.type;
  const questionId = element.questionId;
  // Accept both the runtime screen shape (element.responseOptions) and the
  // assessment-builder design-time shape (element.data.options / itemData.options).
  const rawResponseOptions = element.responseOptions
    || element.data?.options
    || element.itemData?.options
    || [];
  // FECOA-1643: a yes/no item may still arrive holding its raw config dict
  // ({ type:'boolean', yesLabel, noLabel }) rather than an options array (any
  // path that bypassed builder normalization). Left unnormalized, this falls
  // through to the hardcoded default "Yes"/"No" fallback below and drops the
  // item's custom labels + all display styling. Normalize it to a real array so
  // the config-aware single-select branch renders the true labels with full
  // styling — identical to ordinal/nominal. The live participant path already
  // receives a proper array from the serializer, so this is a no-op there.
  const isBooleanQuestion = questionType === 'boolean' || questionType === 'yes_no'
    || questionType === 'yesno' || layout === 'boolean';
  const booleanConfigDict = isBooleanQuestion
    && rawResponseOptions && typeof rawResponseOptions === 'object'
    && !Array.isArray(rawResponseOptions)
    ? rawResponseOptions
    : null;
  const responseOptions = booleanConfigDict
    ? [
        { order: 1, label: booleanConfigDict.yesLabel || 'Yes', value: 1 },
        { order: 2, label: booleanConfigDict.noLabel || 'No', value: 0 },
      ]
    : rawResponseOptions;
  const options = element.options || responseOptions;
  const allowMultiple = element.responseConfig?.allowMultiple || element.multiSelect;
  const optionFontSize = element.responseConfig?.optionFontSize || 14;
  const optionPadding = element.responseConfig?.optionPadding || 8;
  const radioSize = element.responseConfig?.radioSize || 16;
  // Response options inherit the question's font family (seeded from the form
  // formatting default) so swapping the form font also restyles answer choices.
  // Set explicitly because <button>/<input> don't reliably inherit font-family.
  const optionFontFamily = element.style?.fontFamily || 'Arial, sans-serif';
  const optionTextAlign = getOptionTextAlign(element.responseConfig, element.style?.textAlign || 'left');
  const optionsAlignment = element.responseConfig?.optionsAlignment || 'left';
  
  // VAS Slider - Visual Analog Scale
  if (layout === 'vas') {
    return (
      <div className="mt-4">
        <VASSlider
          leftAnchor={element.responseConfig?.leftAnchor || "Left anchor"}
          rightAnchor={element.responseConfig?.rightAnchor || "Right anchor"}
          value={localResponses[questionId] !== undefined ? localResponses[questionId] : 50}
          onChange={(value) => handleResponseChange(questionId, value)}
          disabled={disabled}
          showCurrentValue={element.responseConfig?.showCurrentValue || false}
          colorGradient={element.responseConfig?.colorGradient || false}
          hideMetadata={true}
        />
      </div>
    );
  }
  
  // NRS Scale - Numeric Rating Scale (0-10 buttons)
  if (layout === 'nrs') {
    return (
      <div className="mt-4">
        <NRSScale
          minValue={element.responseConfig?.minValue || 0}
          maxValue={element.responseConfig?.maxValue || 10}
          leftAnchor={element.responseConfig?.leftAnchor || "Left anchor"}
          rightAnchor={element.responseConfig?.rightAnchor || "Right anchor"}
          anchorPlacement={element.responseConfig?.anchorPlacement || "below"}
          leftAnchorOffset={{ x: element.responseConfig?.leftAnchorX || 0, y: element.responseConfig?.leftAnchorY || 0 }}
          rightAnchorOffset={{ x: element.responseConfig?.rightAnchorX || 0, y: element.responseConfig?.rightAnchorY || 0 }}
          value={localResponses[questionId]}
          onChange={(value) => handleResponseChange(questionId, value)}
          disabled={disabled}
          showIndicators={false}
          colorGradient={element.responseConfig?.colorGradient || false}
          hideMetadata={true}
        />
      </div>
    );
  }
  
  // VRS Scale - Verbal Rating Scale (Ordinal/Likert)
  if (layout === 'vrs-unipolar' || layout === 'vrs-bipolar') {
    const formattedOptions = (Array.isArray(responseOptions) ? responseOptions : []).map((opt, idx) => ({
      value: typeof opt === 'object' ? opt.value : idx,
      label: typeof opt === 'string' ? opt : (opt.label || opt)
    }));
    
    return (
      <div className={`mt-4 flex ${
        optionsAlignment === 'center' ? 'justify-center' :
        optionsAlignment === 'right' ? 'justify-end' :
        'justify-start'
      }`}>
        <VRSScale
          options={formattedOptions}
          value={localResponses[questionId]}
          onChange={(value) => handleResponseChange(questionId, value)}
          disabled={disabled}
          orientation={element.responseConfig?.orientation || 'vertical'}
          type={layout === 'vrs-bipolar' ? 'bipolar' : 'unipolar'}
          spacing={element.responseConfig?.spacing || 12}
          buttonHeight={element.responseConfig?.vrsButtonHeight}
          buttonMinWidth={element.responseConfig?.vrsButtonMinWidth}
          fontSize={element.responseConfig?.vrsFontSize}
          fontWeight={element.responseConfig?.vrsFontWeight}
          borderRadius={element.responseConfig?.vrsBorderRadius}
          borderWidth={element.responseConfig?.vrsBorderWidth}
          selectedColor={element.responseConfig?.vrsSelectedColor}
          unselectedColor={element.responseConfig?.vrsUnselectedColor}
          selectedTextColor={element.responseConfig?.vrsSelectedTextColor}
          unselectedTextColor={element.responseConfig?.vrsUnselectedTextColor}
          borderColor={element.responseConfig?.vrsBorderColor}
          selectedBorderColor={element.responseConfig?.vrsSelectedBorderColor}
          hoverColor={element.responseConfig?.vrsHoverColor}
          hoverBorderColor={element.responseConfig?.vrsHoverBorderColor}
          hideMetadata={true}
        />
      </div>
    );
  }
  
  // SEGMENTED CONTROL
  if (layout === 'segmented') {
    return (
      <div className="mt-4">
        <SegmentedControl
          options={responseOptions}
          value={localResponses[questionId]}
          onChange={(value) => handleResponseChange(questionId, value)}
          disabled={disabled}
          fontSize={element.responseConfig?.optionFontSize || 14}
          height={element.responseConfig?.segmentHeight || 44}
          activeColor={element.responseConfig?.activeColor || '#0d9488'}
        />
      </div>
    );
  }

  // EMOJI / FACE SCALE
  if (layout === 'emoji') {
    return (
      <div className="mt-4">
        <EmojiFaceScale
          options={responseOptions}
          value={localResponses[questionId]}
          onChange={(value) => handleResponseChange(questionId, value)}
          disabled={disabled}
          faceSize={element.responseConfig?.faceSize || 48}
          fontSize={element.responseConfig?.optionFontSize || 12}
          showLabels={element.responseConfig?.showLabels !== false}
          showValues={element.responseConfig?.showValues || false}
        />
      </div>
    );
  }

  // THERMOMETER SCALE
  if (layout === 'thermometer') {
    return (
      <div className="mt-4 flex justify-center">
        <ThermometerScale
          minValue={element.responseConfig?.minValue ?? 0}
          maxValue={element.responseConfig?.maxValue ?? 100}
          step={element.responseConfig?.step ?? 10}
          value={localResponses[questionId]}
          onChange={(value) => handleResponseChange(questionId, value)}
          disabled={disabled}
          topAnchor={element.responseConfig?.topAnchor || element.responseConfig?.rightAnchor || 'Best'}
          bottomAnchor={element.responseConfig?.bottomAnchor || element.responseConfig?.leftAnchor || 'Worst'}
          height={300}
          fillColor={element.responseConfig?.fillColor || '#0d9488'}
          showCurrentValue={element.responseConfig?.showCurrentValue !== false}
        />
      </div>
    );
  }

  // STEPPED SLIDER
  if (layout === 'stepped-slider') {
    return (
      <div className="mt-4">
        <SteppedSlider
          options={responseOptions}
          value={localResponses[questionId]}
          onChange={(value) => handleResponseChange(questionId, value)}
          disabled={disabled}
          activeColor={element.responseConfig?.activeColor || '#0d9488'}
          showLabels={element.responseConfig?.showLabels !== false}
          showValues={element.responseConfig?.showValues || false}
          fontSize={element.responseConfig?.optionFontSize || 12}
        />
      </div>
    );
  }

  // FREE TEXT - Text input field (auto-expanding)
  if (questionType === 'text') {
    const maxLength = element.responseConfig?.maxLength || 250;
    const currentValue = localResponses[questionId] || '';
    
    return (
      <div className="mt-4">
        <textarea
          value={currentValue}
          onChange={(e) => {
            handleResponseChange(questionId, e.target.value);
            // Auto-expand: reset height then set to scrollHeight, capped at 300px
            e.target.style.height = 'auto';
            const newHeight = Math.min(Math.max(e.target.scrollHeight, 80), 300);
            e.target.style.height = `${newHeight}px`;
            e.target.style.overflowY = e.target.scrollHeight > 300 ? 'auto' : 'hidden';
          }}
          disabled={disabled}
          placeholder="Type your answer here..."
          maxLength={maxLength}
          className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent disabled:bg-gray-100 disabled:cursor-not-allowed resize-none overflow-hidden"
          rows={2}
          style={{ fontSize: `${optionFontSize}px`, minHeight: '80px' }}
        />
        <div className="mt-1 text-xs text-gray-500 text-right">
          {currentValue.length}/{maxLength} characters
        </div>
      </div>
    );
  }
  
  // YES/NO - Binary choice.
  // When the question carries stored responseOptions (the normal case — the
  // builder seeds [{label:'Yes',value:1},{label:'No',value:0}]), fall through to
  // the config-aware single-select branch below so display / alignment / width /
  // padding / button styling all apply exactly like ordinal & nominal. Only when
  // there are NO stored options do we synthesize a default Yes/No radio pair so
  // legacy data still renders (FECOA yes/no parity fix).
  const yesNoHasOptions = Array.isArray(responseOptions) && responseOptions.length > 0;
  if (questionType === 'yes_no' && !yesNoHasOptions) {
    const yesNoOptions = [
      { value: 'yes', label: 'Yes' },
      { value: 'no', label: 'No' }
    ];
    
    return (
      <div className="mt-4 space-y-3">
        {yesNoOptions.map((option) => (
          <label
            key={option.value}
            className={`flex items-center cursor-pointer p-4 rounded-lg border-2 transition-all ${
              localResponses[questionId] === option.value
                ? 'border-primary-600 bg-primary-50'
                : 'border-gray-200 dark:border-gray-700 hover:border-primary-300 hover:bg-gray-50 dark:hover:bg-gray-700'
            } ${disabled ? 'opacity-50 cursor-not-allowed' : ''}`}
            style={{ padding: `${optionPadding}px` }}
          >
            <input
              type="radio"
              name={`question-${questionId}`}
              value={option.value}
              checked={localResponses[questionId] === option.value}
              onChange={(e) => handleResponseChange(questionId, e.target.value)}
              disabled={disabled}
              className="text-primary-600 cursor-pointer"
              style={{ width: `${radioSize}px`, height: `${radioSize}px` }}
            />
            <span className="ml-3 font-medium text-gray-800" style={{ fontSize: `${optionFontSize}px`, textAlign: optionTextAlign }}>
              {option.label}
            </span>
          </label>
        ))}
      </div>
    );
  }
  
  // BOOLEAN - True/False or custom binary options.
  // Triggered by either questionType (runtime shape) or layout (builder shape).
  // As with yes_no, when stored responseOptions exist we defer to the config-aware
  // single-select branch below (so display/alignment/width/padding/buttons apply);
  // this hardcoded path is only the legacy fallback when no options are present.
  // Gate on responseOptions specifically — that's the array the config-aware
  // branch below consumes. A legacy `options`-only dict shape keeps the fallback.
  const booleanHasOptions = Array.isArray(responseOptions) && responseOptions.length > 0;
  if ((questionType === 'boolean' || layout === 'boolean') && !booleanHasOptions) {
    // Prefer responseOptions (properly formatted by serializer) over raw options dict
    const rawBoolOpts = Array.isArray(responseOptions) && responseOptions.length > 0
      ? responseOptions
      : Array.isArray(options) && options.length > 0
        ? options
        : null;
    const boolOptions = rawBoolOpts
      ? rawBoolOpts.map((opt, idx) => ({
          value: typeof opt === 'object' ? opt.value : idx,
          label: typeof opt === 'object' ? (opt.label || opt.value) : opt
        }))
      : [{ value: 1, label: 'Yes' }, { value: 0, label: 'No' }];
    
    return (
      <div className="mt-4 space-y-3">
        {boolOptions.map((option, idx) => (
          <label
            key={idx}
            className={`flex items-center cursor-pointer p-4 rounded-lg border-2 transition-all ${
              String(localResponses[questionId]) === String(option.value)
                ? 'border-primary-600 bg-primary-50'
                : 'border-gray-200 dark:border-gray-700 hover:border-primary-300 hover:bg-gray-50 dark:hover:bg-gray-700'
            } ${disabled ? 'opacity-50 cursor-not-allowed' : ''}`}
            style={{ padding: `${optionPadding}px` }}
          >
            <input
              type="radio"
              name={`question-${questionId}`}
              value={option.value}
              checked={String(localResponses[questionId]) === String(option.value)}
              onChange={(e) => {
                const value = e.target.value;
                const parsedValue = !isNaN(value) && value !== '' ? Number(value) : value;
                handleResponseChange(questionId, parsedValue);
              }}
              disabled={disabled}
              className="text-primary-600 cursor-pointer"
              style={{ width: `${radioSize}px`, height: `${radioSize}px` }}
            />
            <span className="ml-3 font-medium text-gray-800" style={{ fontSize: `${optionFontSize}px`, textAlign: optionTextAlign }}>
              {option.label}
            </span>
          </label>
        ))}
      </div>
    );
  }
  
  // NUMERIC - Number input
  if (questionType === 'numeric') {
    const minVal = element.responseConfig?.minValue ?? options?.min_value;
    const maxVal = element.responseConfig?.maxValue ?? options?.max_value;
    
    return (
      <div className="mt-4">
        <input
          type="number"
          value={localResponses[questionId] ?? ''}
          onChange={(e) => handleResponseChange(questionId, e.target.value === '' ? '' : Number(e.target.value))}
          disabled={disabled}
          min={minVal}
          max={maxVal}
          placeholder="Enter a number..."
          className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent disabled:bg-gray-100 disabled:cursor-not-allowed"
          style={{ fontSize: `${optionFontSize}px` }}
        />
      </div>
    );
  }
  
  // DATE - Date picker
  if (questionType === 'date') {
    return (
      <div className="mt-4">
        <input
          type="date"
          value={localResponses[questionId] || ''}
          onChange={(e) => handleResponseChange(questionId, e.target.value)}
          disabled={disabled}
          min={element.responseConfig?.minDate}
          max={element.responseConfig?.maxDate}
          className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent disabled:bg-gray-100 disabled:cursor-not-allowed"
          style={{ fontSize: `${optionFontSize}px` }}
        />
      </div>
    );
  }
  
  // EMAIL - Email input
  if (questionType === 'email') {
    return (
      <div className="mt-4">
        <input
          type="email"
          value={localResponses[questionId] || ''}
          onChange={(e) => handleResponseChange(questionId, e.target.value)}
          disabled={disabled}
          placeholder="Enter your email address..."
          className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent disabled:bg-gray-100 disabled:cursor-not-allowed"
          style={{ fontSize: `${optionFontSize}px` }}
        />
      </div>
    );
  }
  
  // CHECKBOX / NOMINAL / MULTIPLE_CHOICE (Multi-select)
  if (questionType === 'checkbox' ||
      (questionType === 'nominal' && allowMultiple) ||
      (questionType === 'multiple_choice' && allowMultiple)) {
    const checkOptions = Array.isArray(responseOptions) ? responseOptions : [];
    const currentSelections = Array.isArray(localResponses[questionId]) ? localResponses[questionId] : [];
    const optionsAlignment = element.responseConfig?.optionsAlignment || 'left';

    // Mirror the single-select button branch so layout templates that set
    // responseConfig.buttonStyle apply to multi-select questions too. Without
    // this, a "Wide button nominal" template renders correctly on the canvas
    // but falls back to plain checkboxes in Preview and SurveyTaking.
    const mcButtonStyle = element.responseConfig?.buttonStyle;
    const mcDisplay = element.responseConfig?.display;
    const showAsButton = (mcButtonStyle && ['outline', 'filled', 'solid'].includes(mcButtonStyle)) ||
                         (mcDisplay && mcDisplay.includes('button'));

    if (showAsButton) {
      const rc = element.responseConfig || {};
      const equalWidth = rc.equalWidth;
      const btnBorderRadius = rc.btnBorderRadius ?? 4;
      const btnBorderWidth = rc.btnBorderWidth ?? 1;
      const btnFontWeight = rc.btnFontWeight || '400';
      const btnFontSize = rc.optionFontSize || 14;
      const btnPadding = rc.optionPadding || 8;
      const btnTextAlign = getOptionTextAlign(rc);
      const btnSpacing = getOptionSpacing(rc);

      return (
        <div className="mt-4" style={{ textAlign: optionsAlignment }}>
          <div className="inline-flex flex-col items-start" style={{ gap: `${btnSpacing}px` }}>
            {checkOptions.map((option, idx) => {
              const optionValue = typeof option === 'object' ? option.value : option;
              const optionLabel = typeof option === 'object' ? (option.label || option.value) : option;
              const isChecked = currentSelections.includes(optionValue) || currentSelections.includes(String(optionValue));
              const buttonColors = getOptionButtonColors(rc, isChecked);

              return (
                <button
                  key={idx}
                  type="button"
                  role="checkbox"
                  aria-checked={isChecked}
                  onClick={() => {
                    if (disabled) return;
                    const newSelections = isChecked
                      ? currentSelections.filter(v => v !== optionValue && v !== String(optionValue))
                      : [...currentSelections, optionValue];
                    handleResponseChange(questionId, newSelections);
                  }}
                  disabled={disabled}
                  className={`
                    transition-colors cursor-pointer
                    ${equalWidth ? 'w-full' : ''}
                    ${disabled ? 'opacity-50 cursor-not-allowed' : ''}
                  `}
                  style={{
                    fontSize: `${btnFontSize}px`,
                    fontFamily: optionFontFamily,
                    padding: `${btnPadding}px ${btnPadding * 2}px`,
                    ...getOptionButtonSizingStyle(rc),
                    textAlign: btnTextAlign,
                    borderRadius: `${btnBorderRadius}px`,
                    borderWidth: `${btnBorderWidth}px`,
                    borderStyle: 'solid',
                    fontWeight: btnFontWeight,
                    ...buttonColors,
                  }}
                >
                  <span dangerouslySetInnerHTML={{ __html: optionLabel }} />
                </button>
              );
            })}
          </div>
        </div>
      );
    }

    return (
      <div className="mt-4" style={{ textAlign: optionsAlignment }}>
        <div className="inline-flex flex-col space-y-1 items-start">
          {checkOptions.map((option, idx) => {
            const optionValue = typeof option === 'object' ? option.value : option;
            const optionLabel = typeof option === 'object' ? (option.label || option.value) : option;
            const isChecked = currentSelections.includes(optionValue) || currentSelections.includes(String(optionValue));

            return (
              <label
                key={idx}
                className={`flex items-center cursor-pointer hover:bg-gray-50 rounded transition-colors ${disabled ? 'opacity-50 cursor-not-allowed' : ''}`}
                style={{ padding: `${optionPadding}px` }}
              >
                <input
                  type="checkbox"
                  checked={isChecked}
                  onChange={(e) => {
                    let newSelections;
                    if (e.target.checked) {
                      newSelections = [...currentSelections, optionValue];
                    } else {
                      newSelections = currentSelections.filter(v => v !== optionValue && v !== String(optionValue));
                    }
                    handleResponseChange(questionId, newSelections);
                  }}
                  disabled={disabled}
                  className="cursor-pointer"
                  style={{ width: `${radioSize}px`, height: `${radioSize}px`, marginRight: '8px' }}
                />
                <span
                  style={{ fontSize: `${optionFontSize}px`, fontFamily: optionFontFamily, textAlign: optionTextAlign }}
                  dangerouslySetInnerHTML={{ __html: optionLabel }}
                />
              </label>
            );
          })}
        </div>
      </div>
    );
  }

  // RATING - Numeric rating scale (fallback if no NRS layout specified)
  if (questionType === 'rating') {
    const minVal = element.responseConfig?.minValue || options?.min_value || 0;
    const maxVal = element.responseConfig?.maxValue || options?.max_value || 10;
    
    // Generate rating buttons
    const ratingButtons = [];
    for (let i = minVal; i <= maxVal; i++) {
      ratingButtons.push(i);
    }
    
    return (
      <div className="mt-4">
        <div className="flex flex-wrap justify-center gap-2">
          {ratingButtons.map((num) => (
            <button
              key={num}
              onClick={() => !disabled && handleResponseChange(questionId, num)}
              disabled={disabled}
              className={`w-12 h-12 rounded-lg font-bold text-lg transition-all ${
                localResponses[questionId] === num
                  ? 'bg-primary-600 text-white border-2 border-primary-700 scale-105'
                  : 'bg-white dark:bg-gray-800 text-gray-700 border-2 border-gray-300 hover:border-primary-400 hover:bg-primary-50'
              } ${disabled ? 'opacity-50 cursor-not-allowed' : 'cursor-pointer'}`}
            >
              {num}
            </button>
          ))}
        </div>
        {(element.responseConfig?.leftAnchor || element.responseConfig?.rightAnchor) && (
          <div className="flex justify-between mt-2 text-sm text-gray-600">
            <span>{element.responseConfig?.leftAnchor || ''}</span>
            <span>{element.responseConfig?.rightAnchor || ''}</span>
          </div>
        )}
      </div>
    );
  }
  
  // BODY MAP - Interactive anterior/posterior body diagram
  if (questionType === 'body_map') {
    const current = Array.isArray(localResponses[questionId]) ? localResponses[questionId] : [];
    const overrides = element.responseConfig?.regionOverrides || {};
    const panelPosition = element.responseConfig?.selectedPanelPosition || null;
    return (
      <div className="mt-4">
        <BodyMap
          value={current}
          onChange={(next) => handleResponseChange(questionId, next)}
          disabled={disabled}
          overrides={overrides}
          panelPosition={panelPosition}
          containerWidth={element.size?.width}
        />
      </div>
    );
  }

  // DROPDOWN
  if (questionType === 'dropdown') {
    const dropdownOptions = Array.isArray(responseOptions) ? responseOptions : [];
    
    return (
      <div className="mt-4">
        <select
          value={localResponses[questionId] || ''}
          onChange={(e) => handleResponseChange(questionId, e.target.value)}
          disabled={disabled}
          className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent disabled:bg-gray-100 disabled:cursor-not-allowed"
          style={{ fontSize: `${optionFontSize}px` }}
        >
          <option value="">Select an option...</option>
          {dropdownOptions.map((option, idx) => {
            const optionValue = typeof option === 'object' ? option.value : option;
            const optionLabel = typeof option === 'object' ? (option.label || option.value) : option;
            return (
              <option key={idx} value={optionValue}>{optionLabel}</option>
            );
          })}
        </select>
      </div>
    );
  }
  
  // DEFAULT: ORDINAL / MULTIPLE CHOICE / LIKERT / NOMINAL (Single-select)
  // This handles: multiple_choice, likert, nominal (single), and any unrecognized types with options
  if (Array.isArray(responseOptions) && responseOptions.length > 0) {
    // Check if should render as buttons
    const buttonStyle = element.responseConfig?.buttonStyle;
    const display = element.responseConfig?.display;
    const showAsButton = (buttonStyle && ['outline', 'filled', 'solid'].includes(buttonStyle)) || 
                        (display && display.includes('button'));

    // Direction/sizing comes from a shared helper so the survey preview and the
    // participant view render IDENTICALLY to the builder canvas. `boolean`
    // (yes/no) resolves to vertical internally, so yes/no behaves like
    // ordinal/nominal: alignment works for radios and horizontal stays a row.
    const groupAlignment = element.responseConfig?.optionsAlignment || 'left';
    const {
      className: optionsContainerClassName,
      style: optionsContainerStyle,
      buttonFillsWidth,
    } = getOptionsGroupLayout(element.responseConfig || {});

    if (showAsButton) {
      // Render as buttons with customizable colors
      const rc = element.responseConfig || {};
      const btnBorderRadius = rc.btnBorderRadius ?? 4;
      const btnBorderWidth = rc.btnBorderWidth ?? 1;
      const btnFontWeight = rc.btnFontWeight || '400';
      const optionFontSize = rc.optionFontSize || 14;
      const optionPadding = rc.optionPadding || 8;
      const optionTextAlign = getOptionTextAlign(rc);

      return (
        <div className="mt-4" style={{ textAlign: groupAlignment }}>
          <div className={optionsContainerClassName} style={optionsContainerStyle}>
            {responseOptions.map((option, idx) => {
              const optionValue = typeof option === 'object' ? option.value : idx;
              const optionLabel = typeof option === 'object' ? (option.label || option.value) : option;
              const isFirstValueMatch = responseOptions.findIndex((o, i) => (typeof o === 'object' ? o.value : i) == optionValue) === idx;
              const isSelected = isSingleSelectOptionSelected(selectedOptionIndex[questionId], idx, localResponses[questionId], optionValue, isFirstValueMatch);
              const buttonColors = getOptionButtonColors(rc, isSelected);

              return (
                <button
                  key={idx}
                  onClick={() => {
                    if (!disabled) {
                      const parsedValue = !isNaN(optionValue) && optionValue !== '' ? Number(optionValue) : optionValue;
                      handleResponseChange(questionId, parsedValue, idx);
                    }
                  }}
                  disabled={disabled}
                  className={`
                    transition-colors cursor-pointer
                    ${buttonFillsWidth ? 'w-full' : 'inline-block'}
                    ${disabled ? 'opacity-50 cursor-not-allowed' : ''}
                  `}
                  style={{
                    fontSize: `${optionFontSize}px`,
                    fontFamily: optionFontFamily,
                    padding: `${optionPadding}px ${optionPadding * 2}px`,
                    // Mirror the builder canvas: equal-width (vertical/grid)
                    // buttons may shrink to share the column; nowrap keeps labels
                    // intact. Horizontal buttons keep their natural width.
                    minWidth: buttonFillsWidth ? '0' : 'auto',
                    whiteSpace: 'nowrap',
                    ...getOptionButtonSizingStyle(rc),
                    textAlign: optionTextAlign,
                    borderRadius: `${btnBorderRadius}px`,
                    borderWidth: `${btnBorderWidth}px`,
                    borderStyle: 'solid',
                    fontWeight: btnFontWeight,
                    ...buttonColors,
                  }}
                >
                  <span dangerouslySetInnerHTML={{ __html: optionLabel }} />
                </button>
              );
            })}
          </div>
        </div>
      );
    }

    // Render as radio buttons (default)
    return (
      <div className="mt-4" style={{ textAlign: groupAlignment }}>
        <div className={optionsContainerClassName} style={optionsContainerStyle}>
          {responseOptions.map((option, idx) => {
            const optionValue = typeof option === 'object' ? option.value : idx;
            const optionLabel = typeof option === 'object' ? (option.label || option.value) : option;
            const isFirstValueMatch = responseOptions.findIndex((o, i) => (typeof o === 'object' ? o.value : i) == optionValue) === idx;
            const isSelected = isSingleSelectOptionSelected(selectedOptionIndex[questionId], idx, localResponses[questionId], optionValue, isFirstValueMatch);

            return (
              <label
                key={idx}
                className={`flex items-center cursor-pointer hover:bg-gray-50 rounded transition-colors ${disabled ? 'opacity-50 cursor-not-allowed' : ''}`}
                style={{ padding: `${optionPadding}px` }}
              >
                <input
                  type="radio"
                  name={`question-${questionId}`}
                  value={optionValue}
                  checked={isSelected}
                  onChange={(e) => {
                    const value = e.target.value;
                    const parsedValue = !isNaN(value) && value !== '' ? Number(value) : value;
                    handleResponseChange(questionId, parsedValue, idx);
                  }}
                  disabled={disabled}
                  className="cursor-pointer"
                  style={{ width: `${radioSize}px`, height: `${radioSize}px`, marginRight: '8px' }}
                />
                <span
                  style={{ fontSize: `${optionFontSize}px`, fontFamily: optionFontFamily, textAlign: optionTextAlign }}
                  dangerouslySetInnerHTML={{ __html: optionLabel }}
                />
              </label>
            );
          })}
        </div>
      </div>
    );
  }

  // No response options and no recognized type - show nothing or a placeholder
  return null;
};

const ScreenRenderer = ({
  screenData,
  responses,
  onResponseChange,
  onNavigate,
  validationErrors,
  disabled = false,
  isFirstQuestion = false,
  allowBackNavigation = true,
  canvasHeader = null,
  viewportClassName = "flex-1 overflow-auto bg-gray-100 flex items-center justify-center p-8",
  canvasClassName = "bg-white dark:bg-gray-800 shadow-lg dark:shadow-gray-900/20 rounded-lg overflow-hidden"
}) => {
  const [localResponses, setLocalResponses] = useState(responses || {});
  // Tracks which option index was clicked per question, so options that share the
  // same value don't all highlight at once (FECOA-1521). On restore — before any
  // click — selection falls back to matching the saved value.
  const [selectedOptionIndex, setSelectedOptionIndex] = useState({});
  const containerRef = useRef(null);
  const [viewportSize, setViewportSize] = useState({ width: 0, height: 0, contentWidth: 0, contentHeight: 0 });

  // Scale is driven by the root container's measured rect — both dimensions.
  // window.innerHeight would over-report available height when a header sits
  // above us (which is exactly how preview and survey-taking drifted).
  // We also capture the padding-excluded *content* box: the responsive canvas
  // fills that width exactly (no hard-coded padding assumption), so it works
  // whatever padding the caller's viewport uses.
  useEffect(() => {
    const node = containerRef.current;
    if (!node) return undefined;

    const update = () => {
      const rect = node.getBoundingClientRect();
      const cs = window.getComputedStyle(node);
      const padX = (parseFloat(cs.paddingLeft) || 0) + (parseFloat(cs.paddingRight) || 0);
      const padY = (parseFloat(cs.paddingTop) || 0) + (parseFloat(cs.paddingBottom) || 0);
      setViewportSize({
        width: rect.width,
        height: rect.height,
        contentWidth: Math.max(0, rect.width - padX),
        contentHeight: Math.max(0, rect.height - padY),
      });
    };
    update();

    const ro = new ResizeObserver(update);
    ro.observe(node);
    return () => ro.disconnect();
  }, []);

  // Sync with parent responses
  useEffect(() => {
    setLocalResponses(responses || {});
  }, [responses]);

  // Handle response change. `optionIndex` is supplied by single-select option
  // lists so the highlight tracks the exact option clicked rather than its value
  // (values may be duplicated across options — FECOA-1521).
  const handleResponseChange = (questionId, value, optionIndex) => {
    const newResponses = {
      ...localResponses,
      [questionId]: value
    };
    setLocalResponses(newResponses);

    setSelectedOptionIndex((prev) => ({
      ...prev,
      [questionId]: optionIndex
    }));

    if (onResponseChange) {
      onResponseChange(questionId, value);
    }
  };

  // Check if all required questions are answered.
  // Source of truth is screenData.navigationRules[questionId].is_required, which is
  // serialized directly from ScreenQuestion.navigation_rules and does not depend on
  // the fragile layout-element matching that populates element.required.
  // Fallback to element.required handles Assessment Builder preview mode, where
  // screens are the in-memory working state and navigationRules has not been
  // bundled onto the screen object yet.
  const areRequiredQuestionsAnswered = () => {
    if (!screenData || !screenData.elements) return true;

    const navRules = screenData.navigationRules || {};
    const isElementRequired = (el) =>
      navRules[String(el.questionId)]?.is_required === true || el.required === true;

    const questionElements = screenData.elements.filter(el => el.type === 'question');
    const requiredQuestions = questionElements.filter(isElementRequired);

    if (requiredQuestions.length === 0) return true;

    return requiredQuestions.every(el => {
      const response = localResponses[el.questionId];
      return response !== undefined &&
             response !== null &&
             response !== '' &&
             !(Array.isArray(response) && response.length === 0);
    });
  };

  // Handle button clicks - pass action type and element data for navigation
  const handleButtonClick = (element) => {
    if (onNavigate) {
      // Pass the action (from NavigationPanel) or fall back to buttonType
      const actionType = element.action || element.buttonType || 'next';
      // Guard: back navigation is blocked on the very first question or when
      // the assessment disallows back navigation.
      if (actionType === 'back' && (isFirstQuestion || !allowBackNavigation)) {
        return;
      }
      onNavigate(actionType, localResponses, element);
    }
  };

  if (!screenData || !screenData.layout || !screenData.elements) {
    return (
      <div className="flex items-center justify-center h-96 text-gray-500">
        <p>No screen data available</p>
      </div>
    );
  }

  const { layout, elements } = screenData;

  // The canvas is always rendered at its fixed design dimensions
  // (layout.width × layout.height) and then uniformly scaled via a CSS
  // transform. Two scaling modes, one mechanism:
  //
  //   • Fixed device canvas — scale DOWN only (never above 1) so the whole
  //     device screen fits inside the viewport (both dimensions), centred.
  //     A built-in 64px breathing margin keeps it off the viewport edges.
  //
  //   • Responsive canvas — scale to CONTAIN the whole design inside the
  //     available box on BOTH axes, centred. It is a uniform zoom of the
  //     builder artboard: every element keeps its exact position and size
  //     relative to the others, the design always stays centred, it never
  //     crops and never scrolls, and it scales UP to fill larger screens
  //     (the items grow — the layout never reflows). This is how a fitted
  //     page fills a browser, which is exactly what the participant sees and
  //     what the preview must replicate (FECOA-1532 / FECOA-1468).
  const isResponsive = layout?.deviceMode === 'responsive';

  // The root has p-8 padding (32px per side → 64px total). getBoundingClientRect
  // returns the border-box, which includes padding, so subtract both sides.
  const canvasPadding = 64;
  let scale = 1;
  if (isResponsive) {
    const fitW = layout.width > 0 && viewportSize.contentWidth > 0
      ? viewportSize.contentWidth / layout.width
      : 0;
    const fitH = layout.height > 0 && viewportSize.contentHeight > 0
      ? viewportSize.contentHeight / layout.height
      : 0;
    // Bind to the tighter axis so the whole design fits with no scrolling. If
    // one axis hasn't been measured yet, fall back to the other (or 1).
    const fit = fitW > 0 && fitH > 0 ? Math.min(fitW, fitH) : (fitW || fitH);
    scale = fit > 0 ? fit : 1;
  } else {
    scale = layout.width > 0 && layout.height > 0 && viewportSize.width > 0 && viewportSize.height > 0
      ? Math.min(
          1,
          Math.max((viewportSize.width - canvasPadding) / layout.width, 0),
          Math.max((viewportSize.height - canvasPadding) / layout.height, 0)
        )
      : 1;
  }
  const scaledWidth = Math.round(layout.width * scale);
  const scaledHeight = Math.round(layout.height * scale);
  const headerProgress = Math.max(0, Math.min(Number(canvasHeader?.progress) || 0, 100));

  // The wrapper reserves exactly the post-scale footprint so the canvas never
  // overlaps surrounding chrome; the canvas itself is the design-sized surface
  // that the transform scales. Identical mechanism for fixed and responsive —
  // only the `scale` differs.
  const wrapperStyle = { width: `${scaledWidth}px`, height: `${scaledHeight}px`, position: 'relative', flexShrink: 0 };
  const canvasStyle = { width: `${layout.width}px`, height: `${layout.height}px`, position: 'absolute', inset: 0, transform: `scale(${scale})`, transformOrigin: 'top left' };

  return (
    <div
      ref={containerRef}
      className={viewportClassName}
    >
      {/* Scaled wrapper reserves exactly the post-scale space in the layout */}
      <div style={wrapperStyle}>
      {/* Main screen canvas — fixed canvases render full-size and scale down via
          transform; the responsive canvas fills 100% of the available width */}
      <div
        className={canvasClassName}
        style={canvasStyle}
      >
        {canvasHeader?.title && (
          <div
            className="screen-renderer-canvas-header"
            style={{
              position: 'absolute',
              top: '100px',
              left: '50%',
              width: 'min(58%, 680px)',
              transform: 'translateX(-50%)',
              zIndex: 4,
              pointerEvents: 'none',
              textAlign: 'center'
            }}
          >
            <h1
              style={{
                margin: 0,
                color: '#0f766e',
                fontFamily: 'Inter, ui-sans-serif, system-ui, -apple-system, BlinkMacSystemFont, "Segoe UI", sans-serif',
                fontSize: '24px',
                fontWeight: 650,
                letterSpacing: '-0.02em',
                lineHeight: 1.2
              }}
            >
              {canvasHeader.title}
            </h1>
            {canvasHeader.showProgress && (
              <div
                className="survey-taking-title-divider"
                aria-hidden="true"
                style={{
                  position: 'relative',
                  margin: '16px auto 0',
                  height: '4px',
                  width: '100%',
                  overflow: 'hidden',
                  borderRadius: '999px',
                  backgroundColor: '#e5e7eb'
                }}
              >
                <div
                  style={{
                    height: '100%',
                    width: `${headerProgress}%`,
                    borderRadius: '999px',
                    backgroundColor: '#0f766e',
                    transition: 'width 500ms ease-out'
                  }}
                />
              </div>
            )}
          </div>
        )}
        {/* Render all screen elements */}
        {elements.map((element) => (
          <div
            key={element.id}
            style={{
              position: 'absolute',
              left: `${element.position.x}px`,
              top: `${element.position.y}px`,
              width: `${element.size.width}px`,
              zIndex: element.type === 'button' ? 10 : 1,
              height: element.type === 'button' ? `${element.size.height}px` : undefined,
              minHeight: element.type === 'button' ? undefined : `${element.size.height}px`,
              padding: element.type === 'button' ? '0' : (element.style?.padding ? `${element.style.padding}px` : '0'),
              backgroundColor: element.type === 'button' ? 'transparent' : (element.style?.backgroundColor || 'transparent'),
              borderRadius: element.type === 'button' ? '0' : (element.style?.borderRadius ? `${element.style.borderRadius}px` : '0'),
              borderWidth: element.type === 'button' ? '0' : (element.style?.borderWidth ? `${element.style.borderWidth}px` : '0'),
              borderColor: element.type === 'button' ? 'transparent' : (element.style?.borderColor || 'transparent'),
              borderStyle: element.type === 'button' ? 'none' : (element.style?.borderStyle || 'solid')
            }}
          >
            {/* Question Element */}
            {element.type === 'question' && !element.isUngrouped && (() => {
              // Accept both runtime (questionText) and builder (content / data.text)
              // shapes so preview and survey-taking render identical text.
              const questionText = element.questionText
                || element.content
                || element.data?.text
                || element.itemData?.text;
              return (
                <div style={{
                  fontSize: `${element.style?.fontSize || 16}px`,
                  fontFamily: element.style?.fontFamily || 'Arial, sans-serif',
                  color: element.style?.color || '#1f2937',
                  lineHeight: element.style?.lineHeight || 1.5
                }}>
                  {questionText && (
                    <div
                      style={{
                        fontWeight: element.style?.fontWeight || '400',
                        textAlign: element.style?.textAlign || 'left',
                        lineHeight: element.style?.lineHeight || 1.5,
                        marginBottom: '12px'
                      }}
                      dangerouslySetInnerHTML={{ __html: questionText }}
                    />
                  )}
                  {renderQuestionResponse(element, localResponses, handleResponseChange, disabled, selectedOptionIndex)}
                </div>
              );
            })()}
            
            {/* Ungrouped Question - render subElements */}
            {element.type === 'question' && element.isUngrouped && element.subElements && (() => {
              const SCALE_SUB_TYPES = ['question-vas', 'question-nrs', 'question-vrs', 'question-segmented', 'question-emoji', 'question-thermometer', 'question-stepped-slider', 'question-text-input'];
              const questionId = element.questionId;
              const savedValue = localResponses[questionId];
              // First option sub carrying the saved value — used only on restore so
              // duplicate-value options don't all highlight at once (FECOA-1521).
              const firstValueMatchIdx = element.subElements.findIndex((s) =>
                s.type === 'question-option' && ((s.value !== undefined ? s.value : s.content) == savedValue));
              return (
              <div className="relative w-full h-full" style={{ padding: element.style?.padding ? `${element.style.padding}px` : '0' }}>
                {element.subElements.map((sub, subIdx) => {
                  const optFontSize = element.responseConfig?.optionFontSize || sub.style?.fontSize || 14;
                  const optPadding = element.responseConfig?.optionPadding || 8;
                  const buttonStyle = element.responseConfig?.buttonStyle;
                  const showAsButton = buttonStyle && ['outline', 'filled', 'solid'].includes(buttonStyle);
                  const optionValue = sub.value !== undefined ? sub.value : sub.content;
                  const isSelected = isSingleSelectOptionSelected(selectedOptionIndex[questionId], subIdx, savedValue, optionValue, subIdx === firstValueMatchIdx);
                  const buttonColors = getOptionButtonColors(element.responseConfig, isSelected);
                  const btnBorderRadius = element.responseConfig?.btnBorderRadius ?? 4;
                  const btnBorderWidth = element.responseConfig?.btnBorderWidth ?? 1;
                  const btnFontWeight = element.responseConfig?.btnFontWeight || '400';
              const optionBoxWidth = getOptionBoxWidth(element.responseConfig);

                  const handleSelect = () => {
                    if (disabled) return;
                    const parsedValue = !isNaN(optionValue) && optionValue !== '' ? Number(optionValue) : optionValue;
                    handleResponseChange(questionId, parsedValue, subIdx);
                  };

                  const measuredOptionBoxWidth = sub.type === 'question-option' && sub.measured
                    ? getFallbackMeasuredOptionBoxWidth(sub.content, element.responseConfig, sub.size?.width)
                    : undefined;
                  const wrapperWidth = sub.measured || sub.type === 'question-text' || SCALE_SUB_TYPES.includes(sub.type)
                    ? `${sub.size.width}px`
                    : 'auto';
                  const effectiveWrapperWidth = sub.type === 'question-nrs-anchor'
                    ? 'auto'
                    : sub.type === 'question-option'
                    ? (optionBoxWidth || measuredOptionBoxWidth || wrapperWidth)
                    : wrapperWidth;

                  return (
                    <div
                      key={sub.id}
                      style={{
                        position: 'absolute',
                        left: `${sub.position.x}px`,
                        top: `${sub.position.y}px`,
                        width: effectiveWrapperWidth,
                      }}
                    >
                      {sub.type === 'question-text' ? (
                        <div style={{
                          fontSize: `${element.style?.fontSize || 16}px`,
                          fontFamily: element.style?.fontFamily || 'Arial, sans-serif',
                          fontWeight: element.style?.fontWeight || '400',
                          color: element.style?.color || '#1f2937',
                          lineHeight: element.style?.lineHeight || 1.5,
                          textAlign: element.style?.textAlign || 'left',
                        }}>
                          <span dangerouslySetInnerHTML={{ __html: sub.content }} />
                        </div>
                      ) : sub.type === 'question-vas' ? (
                        <VASSlider
                          leftAnchor={element.responseConfig?.leftAnchor || 'Left anchor'}
                          rightAnchor={element.responseConfig?.rightAnchor || 'Right anchor'}
                          value={localResponses[questionId] !== undefined ? localResponses[questionId] : 50}
                          onChange={(v) => handleResponseChange(questionId, v)}
                          disabled={disabled}
                          showCurrentValue={element.responseConfig?.showCurrentValue || false}
                          colorGradient={element.responseConfig?.colorGradient || false}
                          hideMetadata={true}
                        />
                      ) : sub.type === 'question-nrs' ? (
                        <NRSScale
                          minValue={element.responseConfig?.minValue || 0}
                          maxValue={element.responseConfig?.maxValue || 10}
                          leftAnchor={element.responseConfig?.leftAnchor || 'Left anchor'}
                          rightAnchor={element.responseConfig?.rightAnchor || 'Right anchor'}
                          anchorPlacement={element.responseConfig?.anchorPlacement || 'below'}
                          leftAnchorOffset={{ x: element.responseConfig?.leftAnchorX || 0, y: element.responseConfig?.leftAnchorY || 0 }}
                          rightAnchorOffset={{ x: element.responseConfig?.rightAnchorX || 0, y: element.responseConfig?.rightAnchorY || 0 }}
                          value={localResponses[questionId]}
                          onChange={(v) => handleResponseChange(questionId, v)}
                          disabled={disabled}
                          orientation={element.responseConfig?.orientation || 'horizontal'}
                          showIndicators={false}
                          colorGradient={element.responseConfig?.colorGradient || false}
                          hideAnchors={element.subElements?.some(s => s.type === 'question-nrs-anchor')}
                          hideMetadata={true}
                        />
                      ) : sub.type === 'question-nrs-anchor' ? (
                        <div
                          className="text-sm font-medium text-gray-700 leading-tight whitespace-nowrap"
                          style={{
                            textAlign: sub.anchorSide === 'right' ? 'right' : 'left',
                            fontFamily: element.style?.fontFamily || 'Arial, sans-serif',
                          }}
                        >
                          {String(
                            (sub.anchorSide === 'right'
                              ? element.responseConfig?.rightAnchor
                              : element.responseConfig?.leftAnchor) ?? sub.content ?? '',
                          ).split(/<br\s*\/?>/i).map((part, i, arr) => (
                            <span key={i}>{part.trim()}{i < arr.length - 1 ? <br /> : null}</span>
                          ))}
                        </div>
                      ) : sub.type === 'question-vrs' ? (
                        <VRSScale
                          options={(Array.isArray(responseOptions) ? responseOptions : []).map((opt, idx) => ({
                            value: typeof opt === 'object' ? opt.value : idx,
                            label: typeof opt === 'string' ? opt : (opt.label || opt),
                          }))}
                          value={localResponses[questionId]}
                          onChange={(v) => handleResponseChange(questionId, v)}
                          disabled={disabled}
                          orientation={element.responseConfig?.orientation || 'vertical'}
                          type={element.responseConfig?.layout === 'vrs-bipolar' ? 'bipolar' : 'unipolar'}
                          spacing={element.responseConfig?.spacing || 12}
                          hideMetadata={true}
                        />
                      ) : sub.type === 'question-segmented' ? (
                        <SegmentedControl
                          options={responseOptions}
                          value={localResponses[questionId]}
                          onChange={(v) => handleResponseChange(questionId, v)}
                          disabled={disabled}
                          fontSize={element.responseConfig?.optionFontSize || 14}
                          height={element.responseConfig?.segmentHeight || 44}
                          activeColor={element.responseConfig?.activeColor || '#0d9488'}
                        />
                      ) : sub.type === 'question-emoji' ? (
                        <EmojiFaceScale
                          options={responseOptions}
                          value={localResponses[questionId]}
                          onChange={(v) => handleResponseChange(questionId, v)}
                          disabled={disabled}
                          faceSize={element.responseConfig?.faceSize || 48}
                          fontSize={element.responseConfig?.optionFontSize || 12}
                          showLabels={element.responseConfig?.showLabels !== false}
                          showValues={element.responseConfig?.showValues || false}
                        />
                      ) : sub.type === 'question-thermometer' ? (
                        <ThermometerScale
                          minValue={element.responseConfig?.minValue ?? 0}
                          maxValue={element.responseConfig?.maxValue ?? 100}
                          step={element.responseConfig?.step ?? 10}
                          value={localResponses[questionId]}
                          onChange={(v) => handleResponseChange(questionId, v)}
                          disabled={disabled}
                          topAnchor={element.responseConfig?.topAnchor || element.responseConfig?.rightAnchor || 'Best'}
                          bottomAnchor={element.responseConfig?.bottomAnchor || element.responseConfig?.leftAnchor || 'Worst'}
                          height={sub.size.height || 300}
                          fillColor={element.responseConfig?.fillColor || '#0d9488'}
                          showCurrentValue={element.responseConfig?.showCurrentValue !== false}
                        />
                      ) : sub.type === 'question-stepped-slider' ? (
                        <SteppedSlider
                          options={responseOptions}
                          value={localResponses[questionId]}
                          onChange={(v) => handleResponseChange(questionId, v)}
                          disabled={disabled}
                          activeColor={element.responseConfig?.activeColor || '#0d9488'}
                          showLabels={element.responseConfig?.showLabels !== false}
                          showValues={element.responseConfig?.showValues || false}
                          fontSize={element.responseConfig?.optionFontSize || 12}
                        />
                      ) : sub.type === 'question-text-input' ? (
                        <textarea
                          value={localResponses[questionId] || ''}
                          onChange={(e) => handleResponseChange(questionId, e.target.value)}
                          placeholder="Enter your response..."
                          disabled={disabled}
                          rows={3}
                          maxLength={element.responseConfig?.maxLength || 500}
                          className={`w-full px-3 py-2 border border-gray-300 dark:border-gray-700 rounded-lg resize-none ${disabled ? 'bg-gray-50 dark:bg-gray-900 cursor-not-allowed' : 'bg-white dark:bg-gray-800'}`}
                          style={{ fontSize: `${element.responseConfig?.optionFontSize || 14}px`, minHeight: '60px' }}
                        />
                      ) : sub.type === 'question-option' ? (
                        showAsButton ? (
                          <button
                            onClick={handleSelect}
                            disabled={disabled}
                            className={`rounded border transition-colors ${disabled ? 'cursor-not-allowed opacity-50' : 'cursor-pointer'} ${
                              buttonStyle === 'outline'
                                ? isSelected
                                  ? 'border-primary-500 bg-primary-50 text-primary-700'
                                  : 'border-gray-300 dark:border-gray-700 bg-white dark:bg-gray-800 text-gray-700 hover:bg-gray-50 dark:hover:bg-gray-700'
                                : isSelected
                                  ? 'border-primary-600 bg-primary-600 text-white'
                                  : 'border-primary-500 bg-primary-500 text-white hover:bg-primary-600'
                            }`}
                            style={{
                              fontSize: `${optFontSize}px`,
                              fontFamily: element.style?.fontFamily || 'Arial, sans-serif',
                              padding: `${optPadding}px ${optPadding * 2}px`,
                              whiteSpace: 'nowrap',
                              ...getOptionButtonSizingStyle(element.responseConfig),
                              ...(measuredOptionBoxWidth && !optionBoxWidth ? { width: measuredOptionBoxWidth, minWidth: measuredOptionBoxWidth } : {}),
                              textAlign: getOptionTextAlign(element.responseConfig),
                              borderRadius: `${btnBorderRadius}px`,
                              borderWidth: `${btnBorderWidth}px`,
                              borderStyle: 'solid',
                              fontWeight: btnFontWeight,
                              ...buttonColors,
                            }}
                          >
                            <span dangerouslySetInnerHTML={{ __html: sub.content }} />
                          </button>
                        ) : (
                          <label className={`flex items-center ${disabled ? 'cursor-not-allowed' : 'cursor-pointer'}`} style={{
                            fontSize: `${optFontSize}px`,
                            fontFamily: element.style?.fontFamily || 'Arial, sans-serif',
                            color: sub.style?.color || '#1f2937'
                          }}>
                            <input
                              type="radio"
                              name={`question-${questionId}`}
                              value={optionValue}
                              checked={isSelected}
                              onChange={handleSelect}
                              disabled={disabled}
                              className="mr-2 text-primary-600"
                              style={{ width: `${element.responseConfig?.radioSize || 16}px`, height: `${element.responseConfig?.radioSize || 16}px` }}
                            />
                            <span dangerouslySetInnerHTML={{ __html: sub.content }} />
                          </label>
                        )
                      ) : null}
                    </div>
                  );
                })}
              </div>
              );
            })()}
            
            {/* Text / Instruction Element */}
            {(element.type === 'text' || element.type === 'instruction') && (
              <p 
                style={{
                  fontSize: `${element.style?.fontSize || 16}px`,
                  fontFamily: element.style?.fontFamily || 'Arial, sans-serif',
                  fontWeight: element.style?.fontWeight || '400',
                  color: element.style?.color || '#000000',
                  textAlign: element.style?.textAlign || 'left'
                }}
                dangerouslySetInnerHTML={{ __html: element.content }}
              />
            )}
            
            {/* Button Element */}
            {element.type === 'button' && (() => {
              // Resolve the effective button action once. action takes precedence over
              // buttonType because legacy elements default buttonType='next' even when
              // the author explicitly set action='back', which used to misclassify Back
              // as a forward button and incorrectly disable it on unanswered required
              // screens.
              const buttonAction = element.action || element.buttonType || 'next';
              const isBackButton = buttonAction === 'back';
              const isForwardButton = buttonAction === 'next' || buttonAction === 'submit' || buttonAction === 'jump';
              // Back buttons are disabled only on the first question or when the
              // assessment has back navigation turned off. Otherwise they're
              // always enabled so participants can review prior answers.
              const shouldDisable = isBackButton
                ? (disabled || isFirstQuestion || !allowBackNavigation)
                : disabled || (isForwardButton && !areRequiredQuestionsAnswered());
              
              return (
                <button
                  onClick={() => handleButtonClick(element)}
                  disabled={shouldDisable}
                  className="w-full h-full flex items-center justify-center font-semibold transition-all disabled:cursor-not-allowed"
                  style={{
                    fontSize: `${element.style?.fontSize || 16}px`,
                    fontFamily: element.style?.fontFamily || 'Arial, sans-serif',
                    fontWeight: element.style?.fontWeight || '600',
                    color: shouldDisable ? '#6b7280' : (element.style?.color || '#ffffff'),
                    backgroundColor: shouldDisable ? '#e5e7eb' : (element.style?.backgroundColor || '#0d9488'),
                    borderRadius: element.style?.borderRadius ? `${element.style.borderRadius}px` : '8px',
                    borderWidth: shouldDisable ? '1px' : (element.style?.borderWidth ? `${element.style.borderWidth}px` : '0'),
                    borderColor: shouldDisable ? '#d1d5db' : (element.style?.borderColor || '#0d9488'),
                    borderStyle: 'solid',
                    opacity: 1,
                    cursor: shouldDisable ? 'not-allowed' : 'pointer',
                    boxShadow: shouldDisable ? 'none' : undefined
                  }}
                >
                  {element.content || (isBackButton ? 'Back' : 'Next')}
                </button>
              );
            })()}
            
            {/* Text Input Element */}
            {element.type === 'text-input' && (
              <div className="h-full flex flex-col justify-center">
                <label className="block text-sm font-medium mb-1" style={{ 
                  fontSize: `${element.style?.fontSize || 14}px`,
                  fontFamily: element.style?.fontFamily || 'Arial, sans-serif',
                  color: element.style?.color || '#1f2937'
                }}>
                  {element.label || 'Text Input'}
                  {element.config?.required && <span className="text-red-500 ml-1">*</span>}
                </label>
                {element.config?.multiline ? (
                  <textarea
                    placeholder={element.placeholder || 'Enter text...'}
                    value={localResponses[element.id] || ''}
                    onChange={(e) => handleResponseChange(element.id, e.target.value)}
                    disabled={disabled}
                    className="w-full px-3 py-2 border border-gray-300 rounded focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent disabled:bg-gray-100"
                    style={{ fontSize: `${element.style?.fontSize || 14}px` }}
                    rows="3"
                    maxLength={element.config?.maxLength || undefined}
                  />
                ) : (
                  <input
                    type="text"
                    placeholder={element.placeholder || 'Enter text...'}
                    value={localResponses[element.id] || ''}
                    onChange={(e) => handleResponseChange(element.id, e.target.value)}
                    disabled={disabled}
                    className="w-full px-3 py-2 border border-gray-300 rounded focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent disabled:bg-gray-100"
                    style={{ fontSize: `${element.style?.fontSize || 14}px` }}
                    maxLength={element.config?.maxLength || undefined}
                  />
                )}
                {validationErrors && validationErrors[element.id] && (
                  <div className="mt-1 text-red-600 text-sm flex items-center">
                    <AlertCircle className="w-4 h-4 mr-1" />
                    {validationErrors[element.id]}
                  </div>
                )}
              </div>
            )}
            
            {/* Number Input Element */}
            {element.type === 'number-input' && (
              <div className="h-full flex flex-col justify-center">
                <label className="block text-sm font-medium mb-1" style={{ 
                  fontSize: `${element.style?.fontSize || 14}px`,
                  fontFamily: element.style?.fontFamily || 'Arial, sans-serif',
                  color: element.style?.color || '#1f2937'
                }}>
                  {element.label || 'Number Input'}
                  {element.config?.required && <span className="text-red-500 ml-1">*</span>}
                </label>
                <input
                  type="number"
                  placeholder={element.placeholder || '0'}
                  value={localResponses[element.id] ?? ''}
                  onChange={(e) => handleResponseChange(element.id, e.target.value === '' ? '' : Number(e.target.value))}
                  disabled={disabled}
                  min={element.config?.min}
                  max={element.config?.max}
                  step={element.config?.step || 1}
                  className="w-full px-3 py-2 border border-gray-300 rounded focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent disabled:bg-gray-100"
                  style={{ fontSize: `${element.style?.fontSize || 14}px` }}
                />
                {validationErrors && validationErrors[element.id] && (
                  <div className="mt-1 text-red-600 text-sm flex items-center">
                    <AlertCircle className="w-4 h-4 mr-1" />
                    {validationErrors[element.id]}
                  </div>
                )}
              </div>
            )}
            
            {/* Date Picker Element */}
            {element.type === 'date-picker' && (
              <div className="h-full flex flex-col justify-center">
                <label className="block text-sm font-medium mb-1" style={{ 
                  fontSize: `${element.style?.fontSize || 14}px`,
                  fontFamily: element.style?.fontFamily || 'Arial, sans-serif',
                  color: element.style?.color || '#1f2937'
                }}>
                  {element.label || 'Date Picker'}
                  {element.config?.required && <span className="text-red-500 ml-1">*</span>}
                </label>
                <input
                  type={element.config?.includeTime ? 'datetime-local' : 'date'}
                  value={localResponses[element.id] || ''}
                  onChange={(e) => handleResponseChange(element.id, e.target.value)}
                  disabled={disabled}
                  min={element.config?.minDate}
                  max={element.config?.maxDate}
                  className="w-full px-3 py-2 border border-gray-300 rounded focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent disabled:bg-gray-100"
                  style={{ fontSize: `${element.style?.fontSize || 14}px` }}
                />
                {validationErrors && validationErrors[element.id] && (
                  <div className="mt-1 text-red-600 text-sm flex items-center">
                    <AlertCircle className="w-4 h-4 mr-1" />
                    {validationErrors[element.id]}
                  </div>
                )}
              </div>
            )}
            
            {/* Checkbox Group Element */}
            {element.type === 'checkbox' && (
              <div className="h-full overflow-auto">
                <label className="block text-sm font-medium mb-2" style={{ 
                  fontSize: `${element.style?.fontSize || 14}px`,
                  fontFamily: element.style?.fontFamily || 'Arial, sans-serif',
                  color: element.style?.color || '#1f2937'
                }}>
                  {element.label || 'Select all that apply'}
                  {element.config?.required && <span className="text-red-500 ml-1">*</span>}
                </label>
                <div className={element.config?.layout === 'horizontal' ? 'flex flex-wrap gap-4' : 'space-y-2'}>
                  {(element.options || []).map((option, idx) => {
                    const currentSelections = localResponses[element.id] || [];
                    const isChecked = currentSelections.includes(option);
                    return (
                      <label key={idx} className="flex items-center cursor-pointer hover:bg-gray-50 dark:hover:bg-gray-700 p-1 rounded transition-colors">
                        <input
                          type="checkbox"
                          checked={isChecked}
                          onChange={(e) => {
                            const newSelections = e.target.checked
                              ? [...currentSelections, option]
                              : currentSelections.filter(s => s !== option);
                            handleResponseChange(element.id, newSelections);
                          }}
                          disabled={disabled}
                          className="w-4 h-4 text-primary-600 border-gray-300 rounded focus:ring-primary-500 mr-2 cursor-pointer disabled:cursor-not-allowed"
                        />
                        <span style={{ fontSize: `${element.style?.fontSize || 14}px` }}>{option}</span>
                      </label>
                    );
                  })}
                </div>
                {validationErrors && validationErrors[element.id] && (
                  <div className="mt-1 text-red-600 text-sm flex items-center">
                    <AlertCircle className="w-4 h-4 mr-1" />
                    {validationErrors[element.id]}
                  </div>
                )}
              </div>
            )}
            
            {/* Scale / Slider Element */}
            {element.type === 'scale' && (
              <div className="h-full flex flex-col justify-center px-2">
                <label className="block text-sm font-medium mb-2" style={{ 
                  fontSize: `${element.style?.fontSize || 14}px`,
                  fontFamily: element.style?.fontFamily || 'Arial, sans-serif',
                  color: element.style?.color || '#1f2937'
                }}>
                  {element.label || 'Rate your experience'}
                  {element.config?.required && <span className="text-red-500 ml-1">*</span>}
                </label>
                <div className="flex items-center space-x-3">
                  {element.config?.showLabels && element.config?.minLabel && (
                    <span className="text-xs text-gray-600 min-w-[60px]">{element.config.minLabel}</span>
                  )}
                  <input
                    type="range"
                    min={element.config?.min || 0}
                    max={element.config?.max || 10}
                    step={element.config?.step || 1}
                    value={localResponses[element.id] ?? Math.floor(((element.config?.min || 0) + (element.config?.max || 10)) / 2)}
                    onChange={(e) => handleResponseChange(element.id, Number(e.target.value))}
                    disabled={disabled}
                    className="flex-1 h-2 bg-gray-200 rounded-lg appearance-none cursor-pointer accent-primary-600 disabled:cursor-not-allowed"
                  />
                  {element.config?.showLabels && element.config?.maxLabel && (
                    <span className="text-xs text-gray-600 min-w-[60px] text-right">{element.config.maxLabel}</span>
                  )}
                </div>
                {element.config?.showValue && (
                  <div className="text-center text-sm font-semibold text-primary-600 mt-2">
                    {localResponses[element.id] ?? Math.floor(((element.config?.min || 0) + (element.config?.max || 10)) / 2)}
                  </div>
                )}
                {validationErrors && validationErrors[element.id] && (
                  <div className="mt-1 text-red-600 text-sm flex items-center">
                    <AlertCircle className="w-4 h-4 mr-1" />
                    {validationErrors[element.id]}
                  </div>
                )}
              </div>
            )}
            
            {/* Divider Element */}
            {element.type === 'divider' && (
              <div 
                className="w-full"
                style={{
                  height: `${element.config?.thickness || 2}px`,
                  backgroundColor: element.style?.backgroundColor || '#d1d5db',
                  borderRadius: `${element.style?.borderRadius || 0}px`
                }}
              />
            )}
          </div>
        ))}
      </div>
      </div>
    </div>
  );
};

export default ScreenRenderer;
